import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import { fetcher } from '../../../../utils/api/fetcher/fetcher';
import type { ApiError } from '../../../../utils/api/ApiError/ApiError';
import {
  sourceSystemShape,
  type SourceSystem,
} from '../../../../utils/api/types/access-api-types';

export const classSchema = z.object({
  class_id: z.string(),
  grade_low: z.string().optional(),
  grade_high: z.string().optional(),
  name: z.string(),
  type: z.string(),
  organizations: z.array(
    z.object({
      organization_id: z.string(),
      name: z.string(),
    })
  ),
  teachers: z.array(
    z.object({
      user_id: z.string(),
      given_name: z.string(),
      family_name: z.string(),
    })
  ),
  studentIds: z.array(z.string()),
  primary_teacher_id: z.string().optional(),
  source_id: z.string().optional(),
  source_id_type: z.union([z.literal('UCN'), z.literal('SourceId')]).optional(),
  source_system: sourceSystemShape.optional(),
});

export const classResponseSchema = z.array(classSchema);
export type Class = z.TypeOf<typeof classSchema>;

export const CLASS_SOURCE_MAP: Record<SourceSystem, string> = {
  Manual: 'MANUAL',
  Ednition: 'EDNITION',
  MyScholastic: 'MYSCHOLASTIC',
  CMDM: 'CMDM',
  ScholasticOkta: 'SCHOLASTIC_OKTA',
  GoogleClassroom: 'GOOGLE_CLASSROOM',
  siteAuth: 'siteAuth',
} as const;

export interface GetClassApiParams {
  source: SourceSystem;
  userId: string;
  orgId: string;
  studentsExists?: boolean;
}

export async function getClassesApi({
  userId,
  orgId,
  source,
  studentsExists,
}: GetClassApiParams): Promise<Class[]> {
  const classes = await fetcher<Class[]>(
    `/composite/classes/source-systems/${CLASS_SOURCE_MAP[source]}/orgs/${orgId}/users/${userId}/class-types/CLASS`,
    {
      service: '/roster',
      version: '/v3',
      queryParameters: {
        studentsExists: studentsExists ? 'true' : 'false',
      },
    }
  );

  return classResponseSchema.parse(classes);
}

export const getClassesApiQueryKey = (args?: GetClassApiParams) => {
  const { userId, orgId, source, studentsExists } = args ?? {};

  return ['/v3', 'classes', userId, orgId, source, studentsExists].filter(
    Boolean
  ) as string[];
};

export function useGetClassesApi(
  { userId, orgId, source, studentsExists }: GetClassApiParams,
  customQueryOptions?: Partial<UseQueryOptions<Class[], ApiError>>
): UseQueryResult<Class[], ApiError> {
  const { enabled = true } = customQueryOptions ?? {};
  return useQuery<Class[], ApiError>({
    queryKey: getClassesApiQueryKey({ userId, orgId, source, studentsExists }),
    queryFn: () =>
      getClassesApi({
        userId,
        orgId,
        source,
        studentsExists,
      }),
    enabled:
      enabled && Boolean(userId && orgId && source) && source !== 'siteAuth',
    ...customQueryOptions,
  });
}

export { type SourceSystem };
